import type { SignInFirstFactor, SignInSecondFactor } from "@clerk/shared/types"

export interface EmailCodeFallback {
  emailAddressId: string
  safeIdentifier: string
}

export interface PasswordResetFallback {
  emailAddressId: string
  safeIdentifier: string
}

export type ClerkSigninPhase =
  | { kind: "email_entry" }
  | { kind: "resolving_factors" }
  | { kind: "email_code"; emailAddressId: string; safeIdentifier: string }
  | {
      kind: "password"
      emailCode?: EmailCodeFallback
      resetPassword?: PasswordResetFallback
    }
  | { kind: "reset_password"; emailAddressId: string; safeIdentifier: string }
  | { kind: "redirecting_sso" }
  | { kind: "second_factor"; factors: SignInSecondFactor[] }

export function pickFirstFactor(factors: SignInFirstFactor[] | null) {
  if (!factors) {
    return undefined
  }
  const usable = factors.filter(
    (factor) =>
      factor.strategy !== "reset_password_email_code" &&
      factor.strategy !== "reset_password_phone_code",
  )
  const singleSignOn = usable.find(
    (factor) => factor.strategy === "enterprise_sso",
  )
  return singleSignOn ?? usable[0]
}

export function findEmailCodeFallback(
  factors: SignInFirstFactor[] | null | undefined,
): EmailCodeFallback | undefined {
  const emailCode = factors?.find(
    (
      factor,
    ): factor is Extract<SignInFirstFactor, { strategy: "email_code" }> =>
      factor.strategy === "email_code",
  )
  return emailCode
    ? {
        emailAddressId: emailCode.emailAddressId,
        safeIdentifier: emailCode.safeIdentifier,
      }
    : undefined
}

export function findPasswordResetFallback(
  factors: SignInFirstFactor[] | null | undefined,
): PasswordResetFallback | undefined {
  const reset = factors?.find(
    (
      factor,
    ): factor is Extract<
      SignInFirstFactor,
      { strategy: "reset_password_email_code" }
    > => factor.strategy === "reset_password_email_code",
  )
  return reset
    ? {
        emailAddressId: reset.emailAddressId,
        safeIdentifier: reset.safeIdentifier,
      }
    : undefined
}

export function clerkSigninHeader(
  phaseKind: Extract<
    ClerkSigninPhase["kind"],
    "email_entry" | "resolving_factors" | "redirecting_sso"
  >,
) {
  switch (phaseKind) {
    case "redirecting_sso":
    case "resolving_factors":
      return "Signing you in…"
    case "email_entry":
      return "Sign in to Coval"
  }
}
